import { useState, useEffect, useRef } from 'react';
import './App.css';
import { Building2, Globe2, Users, Award, ChevronRight, Menu, X, Database, Cloud, Shield, Cpu, 
  BarChart3, CreditCard, Landmark, Building, Briefcase, GraduationCap, Lightbulb, Target,
  CheckCircle2, ArrowRight, MapPin, Mail, Phone, Linkedin, Instagram, Facebook, Twitter, Youtube,
  Server, Code, Settings, HeadphonesIcon, UserCheck, FileCheck, Rocket, Clock, Heart,
  TrendingUp, Layers, Lock, Zap, Bot, PieChart, Network, MonitorSmartphone, Wallet, DollarSign,
  Scale, Banknote, Receipt, ShieldCheck, Globe, Play, ExternalLink, Quote, Send, Loader2,
  CircuitBoard, Sparkles, Eye, BookOpen, FileText, Video, MessageSquare } from 'lucide-react';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: '', message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setFormStatus({ type: 'success', message: 'Thank you for your inquiry. Our team will contact you shortly.' });
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      } else {
        setFormStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setFormStatus({ type: 'error', message: 'Connection error. Please try again later.' });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="app" data-testid="trempplin-app">
      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`} data-testid="main-navbar">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => scrollToSection('home')} data-testid="nav-logo">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" />
          </div>
          
          <div className="nav-menu" data-testid="nav-menu">
            <button onClick={() => scrollToSection('about')}>Company</button>
            <button onClick={() => scrollToSection('services')}>Enterprise Services</button>
            <button onClick={() => scrollToSection('industries')}>Industries</button>
            <button onClick={() => scrollToSection('solutions')}>Solutions</button>
            <button onClick={() => scrollToSection('partners')}>Partners</button>
            <a href="/strategic-partner-alliance" className="nav-link-highlight">Partner Alliance</a>
            <button onClick={() => scrollToSection('talent')}>Talent Solutions</button>
            <button onClick={() => scrollToSection('insights')}>Insights</button>
            <button onClick={() => scrollToSection('careers')}>Careers</button>
            <button className="nav-cta" onClick={() => scrollToSection('contact')} data-testid="nav-contact-btn">
              Contact
            </button>
          </div>

          <button className="nav-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="nav-mobile-menu" data-testid="mobile-menu">
            <button onClick={() => scrollToSection('about')}>Company</button>
            <button onClick={() => scrollToSection('services')}>Enterprise Services</button>
            <button onClick={() => scrollToSection('industries')}>Industries</button>
            <button onClick={() => scrollToSection('solutions')}>Solutions</button>
            <button onClick={() => scrollToSection('partners')}>Partners</button>
            <a href="/strategic-partner-alliance" className="nav-link-highlight">Partner Alliance</a>
            <button onClick={() => scrollToSection('talent')}>Talent Solutions</button>
            <button onClick={() => scrollToSection('insights')}>Insights</button>
            <button onClick={() => scrollToSection('careers')}>Careers</button>
            <button className="nav-cta" onClick={() => scrollToSection('contact')}>Contact</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero" data-testid="hero-section">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badges">
            <span className="badge badge-teal">
              <Shield size={14} />
              ISO 27001:2022 Certified
            </span>
            <span className="badge badge-gold">
              <Award size={14} />
              Oracle Partner
            </span>
          </div>

          <h1 className="hero-title" data-testid="hero-title">
            Global Banking Technology
            <span className="hero-title-accent">&amp; FinTech Company</span>
          </h1>

          <p className="hero-subtitle">
            Delivering Oracle Banking Consulting, Digital Banking, Managed Services, 
            Enterprise Technology Solutions and Talent Development for Banks and 
            Financial Institutions worldwide.
          </p>

          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => scrollToSection('services')} data-testid="hero-cta-primary">
              Explore Enterprise Services
              <ChevronRight size={20} />
            </button>
            <button className="btn btn-outline" onClick={() => scrollToSection('contact')} data-testid="hero-cta-secondary">
              Partner With Us
            </button>
          </div>
        </div>
      </section>

      {/* Global Statistics Bar */}
      <section className="stats-bar" data-testid="stats-bar">
        <div className="stats-container">
          <div className="stat-item">
            <span className="stat-value">2016</span>
            <span className="stat-label">Established</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">45+</span>
            <span className="stat-label">Countries Served</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">70+</span>
            <span className="stat-label">Banking Clients</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">100+</span>
            <span className="stat-label">Enterprise Projects</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">50+</span>
            <span className="stat-label">Technology Experts</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item highlight">
            <Shield size={20} className="stat-icon" />
            <span className="stat-label">ISO 27001:2022</span>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="about" className="section section-dark" data-testid="about-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Who We Are</span>
            <h2>A Global Banking Technology Company</h2>
            <p className="section-intro">
              Trempplin is a Banking Technology company specializing in enterprise consulting, 
              Oracle Banking implementation, managed services, digital transformation and 
              global workforce solutions. We partner with banks and financial institutions 
              worldwide to deliver mission-critical technology programs while building the 
              next generation of Banking Technology professionals.
            </p>
          </div>

          <div className="about-features">
            <div className="about-feature">
              <div className="about-feature-icon">
                <Building2 size={28} />
              </div>
              <h4>Enterprise Technology Solutions</h4>
              <p>End-to-end banking technology services from consulting to implementation and support</p>
            </div>
            <div className="about-feature">
              <div className="about-feature-icon">
                <Globe2 size={28} />
              </div>
              <h4>Global Delivery Capability</h4>
              <p>Serving banks across 45+ countries with 24/7 support and delivery excellence</p>
            </div>
            <div className="about-feature">
              <div className="about-feature-icon">
                <Users size={28} />
              </div>
              <h4>Long-term Client Partnerships</h4>
              <p>Building lasting relationships focused on continuous value creation</p>
            </div>
            <div className="about-feature">
              <div className="about-feature-icon">
                <GraduationCap size={28} />
              </div>
              <h4>Talent Ecosystem</h4>
              <p>Developing the next generation of Banking Technology professionals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Services Section */}
      <section id="services" className="section section-navy" data-testid="services-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Enterprise Services</span>
            <h2>Comprehensive Banking Technology Solutions</h2>
            <p className="section-intro">
              From Oracle Banking implementations to managed services, we deliver 
              enterprise-grade solutions for global financial institutions.
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card glass-card">
              <div className="service-icon">
                <Database size={28} />
              </div>
              <h3>Oracle Banking Solutions</h3>
              <p>Complete Oracle Banking product suite implementation and customization</p>
              <ul className="service-list">
                <li>Oracle FLEXCUBE Universal Banking</li>
                <li>Oracle OBDX Digital Experience</li>
                <li>Oracle OFSAA Analytics</li>
                <li>Oracle OBTF Trade Finance</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>

            <div className="service-card glass-card">
              <div className="service-icon">
                <HeadphonesIcon size={28} />
              </div>
              <h3>Managed Services</h3>
              <p>24/7 application management, monitoring and support services</p>
              <ul className="service-list">
                <li>Application Support & Maintenance</li>
                <li>Infrastructure Management</li>
                <li>Performance Monitoring</li>
                <li>Incident Management</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>

            <div className="service-card glass-card">
              <div className="service-icon">
                <MonitorSmartphone size={28} />
              </div>
              <h3>Digital Banking</h3>
              <p>Next-generation digital banking solutions for modern customers</p>
              <ul className="service-list">
                <li>Mobile Banking Applications</li>
                <li>Internet Banking Platforms</li>
                <li>Omnichannel Experience</li>
                <li>API Banking Solutions</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>

            <div className="service-card glass-card">
              <div className="service-icon">
                <UserCheck size={28} />
              </div>
              <h3>Staff Augmentation</h3>
              <p>Expert banking technology consultants on-demand</p>
              <ul className="service-list">
                <li>Oracle Banking Specialists</li>
                <li>Technical Architects</li>
                <li>Functional Consultants</li>
                <li>Project Managers</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>

            <div className="service-card glass-card">
              <div className="service-icon">
                <Rocket size={28} />
              </div>
              <h3>Implementation Services</h3>
              <p>End-to-end project delivery with proven methodology</p>
              <ul className="service-list">
                <li>Requirements Analysis</li>
                <li>Solution Design</li>
                <li>Development & Configuration</li>
                <li>Testing & Deployment</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>

            <div className="service-card glass-card">
              <div className="service-icon">
                <Briefcase size={28} />
              </div>
              <h3>Banking Consulting</h3>
              <p>Strategic advisory for digital transformation journeys</p>
              <ul className="service-list">
                <li>Technology Roadmap</li>
                <li>Process Optimization</li>
                <li>Digital Strategy</li>
                <li>Vendor Selection</li>
              </ul>
              <button className="link-btn">
                Learn More <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Banks Trust Trempplin */}
      <section className="section section-dark" data-testid="trust-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Why Banks Trust Us</span>
            <h2>Building Long-term Partnerships with Financial Institutions</h2>
          </div>

          <div className="trust-grid">
            <div className="trust-card">
              <div className="trust-icon">
                <Landmark size={24} />
              </div>
              <h4>Banking Technology Expertise</h4>
              <p>Deep domain knowledge across retail, corporate, trade finance, and digital banking</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Database size={24} />
              </div>
              <h4>Oracle Banking Specialists</h4>
              <p>Complete Oracle Banking product suite expertise with certified professionals</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Target size={24} />
              </div>
              <h4>Enterprise Delivery Excellence</h4>
              <p>On-time, on-budget project delivery with proven methodology</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Globe size={24} />
              </div>
              <h4>Global Consulting Presence</h4>
              <p>Serving banks across 45+ countries with local expertise</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Clock size={24} />
              </div>
              <h4>Managed Services 24/7</h4>
              <p>Round-the-clock support ensuring business continuity</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Zap size={24} />
              </div>
              <h4>Digital Transformation</h4>
              <p>Future-ready solutions for next-generation banking</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Heart size={24} />
              </div>
              <h4>Long-term Partnership Focus</h4>
              <p>We grow with our clients, ensuring sustained success</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <ShieldCheck size={24} />
              </div>
              <h4>Security First - ISO 27001</h4>
              <p>Certified information security practices and compliance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Delivery Lifecycle */}
      <section className="section section-navy" data-testid="lifecycle-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Our Approach</span>
            <h2>Enterprise Delivery Lifecycle</h2>
            <p className="section-intro">
              How we deliver mission-critical banking projects with proven methodology
            </p>
          </div>

          <div className="lifecycle-flow">
            <div className="lifecycle-step">
              <div className="lifecycle-number">01</div>
              <div className="lifecycle-icon"><FileText size={24} /></div>
              <h4>Client Requirement</h4>
              <p>Understanding business needs</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">02</div>
              <div className="lifecycle-icon"><MessageSquare size={24} /></div>
              <h4>Consulting</h4>
              <p>Strategic advisory</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">03</div>
              <div className="lifecycle-icon"><Layers size={24} /></div>
              <h4>Solution Design</h4>
              <p>Architecture & planning</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">04</div>
              <div className="lifecycle-icon"><Code size={24} /></div>
              <h4>Implementation</h4>
              <p>Development & config</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">05</div>
              <div className="lifecycle-icon"><FileCheck size={24} /></div>
              <h4>Testing</h4>
              <p>Quality assurance</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">06</div>
              <div className="lifecycle-icon"><Rocket size={24} /></div>
              <h4>Deployment</h4>
              <p>Go-live support</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">07</div>
              <div className="lifecycle-icon"><Settings size={24} /></div>
              <h4>Managed Services</h4>
              <p>Ongoing operations</p>
            </div>
            <div className="lifecycle-connector"></div>
            
            <div className="lifecycle-step">
              <div className="lifecycle-number">08</div>
              <div className="lifecycle-icon"><TrendingUp size={24} /></div>
              <h4>Support & Growth</h4>
              <p>Continuous improvement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Banking Technology Expertise */}
      <section className="section section-dark" data-testid="expertise-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Technology Expertise</span>
            <h2>Banking Technology Excellence</h2>
            <p className="section-intro">
              Complete Oracle Banking and enterprise technology suite expertise
            </p>
          </div>

          <div className="expertise-grid">
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>FLEXCUBE</h4>
              <p>Universal Core Banking</p>
            </div>
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>OBDX</h4>
              <p>Digital Experience</p>
            </div>
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>OFSAA</h4>
              <p>Financial Analytics</p>
            </div>
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>OBTF</h4>
              <p>Trade Finance</p>
            </div>
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>OBPM</h4>
              <p>Payments</p>
            </div>
            <div className="expertise-card">
              <div className="expertise-badge">Oracle</div>
              <h4>ELCM</h4>
              <p>Limits & Collateral</p>
            </div>
            <div className="expertise-card">
              <h4>Digital Banking</h4>
              <p>Mobile & Web Solutions</p>
            </div>
            <div className="expertise-card">
              <h4>Core Banking</h4>
              <p>Transformation Services</p>
            </div>
            <div className="expertise-card">
              <h4>Testing & QA</h4>
              <p>Quality Assurance</p>
            </div>
            <div className="expertise-card">
              <h4>Managed Services</h4>
              <p>24/7 Support</p>
            </div>
            <div className="expertise-card">
              <h4>Enterprise Integration</h4>
              <p>API & Middleware</p>
            </div>
          </div>
        </div>
      </section>

      {/* Banking Solutions */}
      <section id="solutions" className="section section-navy" data-testid="solutions-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Banking Solutions</span>
            <h2>End-to-End Solutions Across Banking Domains</h2>
          </div>

          <div className="solutions-grid">
            <div className="solution-card">
              <Wallet size={32} />
              <h4>Retail Banking</h4>
              <p>Consumer banking solutions</p>
            </div>
            <div className="solution-card">
              <Building size={32} />
              <h4>Corporate Banking</h4>
              <p>Enterprise banking services</p>
            </div>
            <div className="solution-card">
              <Globe2 size={32} />
              <h4>Trade Finance</h4>
              <p>International trade solutions</p>
            </div>
            <div className="solution-card">
              <CreditCard size={32} />
              <h4>Payments</h4>
              <p>Payment processing systems</p>
            </div>
            <div className="solution-card">
              <Banknote size={32} />
              <h4>Lending</h4>
              <p>Loan origination & management</p>
            </div>
            <div className="solution-card">
              <PieChart size={32} />
              <h4>Treasury</h4>
              <p>Treasury management systems</p>
            </div>
            <div className="solution-card">
              <Landmark size={32} />
              <h4>Islamic Banking</h4>
              <p>Shariah-compliant solutions</p>
            </div>
            <div className="solution-card">
              <Scale size={32} />
              <h4>Risk & Compliance</h4>
              <p>Regulatory compliance</p>
            </div>
            <div className="solution-card">
              <MonitorSmartphone size={32} />
              <h4>Digital Banking</h4>
              <p>Next-gen digital platforms</p>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation & Technology */}
      <section className="section section-innovation" data-testid="innovation-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Innovation</span>
            <h2>Banking Technology Innovation</h2>
            <p className="section-intro">
              Future-ready solutions for next-generation banking
            </p>
          </div>

          <div className="innovation-grid">
            <div className="innovation-card">
              <Bot size={36} />
              <h4>AI & Machine Learning</h4>
              <p>Intelligent automation and predictive analytics</p>
            </div>
            <div className="innovation-card">
              <Zap size={36} />
              <h4>Automation</h4>
              <p>Process automation and RPA solutions</p>
            </div>
            <div className="innovation-card">
              <MonitorSmartphone size={36} />
              <h4>Digital Banking</h4>
              <p>Next-generation digital experiences</p>
            </div>
            <div className="innovation-card">
              <Cloud size={36} />
              <h4>Cloud Solutions</h4>
              <p>Cloud migration and modernization</p>
            </div>
            <div className="innovation-card">
              <Network size={36} />
              <h4>API Banking</h4>
              <p>Open banking and API ecosystems</p>
            </div>
            <div className="innovation-card">
              <BarChart3 size={36} />
              <h4>Data Analytics</h4>
              <p>Business intelligence and reporting</p>
            </div>
            <div className="innovation-card">
              <Lock size={36} />
              <h4>Cyber Security</h4>
              <p>Security and compliance solutions</p>
            </div>
            <div className="innovation-card">
              <CircuitBoard size={36} />
              <h4>Enterprise Architecture</h4>
              <p>Modern architecture design</p>
            </div>
            <div className="innovation-card featured">
              <Lightbulb size={36} />
              <h4>Research & Innovation</h4>
              <p>FinTech labs and emerging tech</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section id="industries" className="section section-navy" data-testid="industries-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Industries</span>
            <h2>Industries We Serve</h2>
          </div>

          <div className="industries-grid">
            <div className="industry-card">
              <Wallet size={28} />
              <span>Retail Banking</span>
            </div>
            <div className="industry-card">
              <Building size={28} />
              <span>Corporate Banking</span>
            </div>
            <div className="industry-card">
              <Landmark size={28} />
              <span>Islamic Banking</span>
            </div>
            <div className="industry-card">
              <MonitorSmartphone size={28} />
              <span>Digital Banking</span>
            </div>
            <div className="industry-card">
              <Cpu size={28} />
              <span>FinTech</span>
            </div>
            <div className="industry-card">
              <Users size={28} />
              <span>Microfinance</span>
            </div>
            <div className="industry-card">
              <Shield size={28} />
              <span>Insurance</span>
            </div>
            <div className="industry-card">
              <Building2 size={28} />
              <span>Government Finance</span>
            </div>
            <div className="industry-card">
              <Landmark size={28} />
              <span>Central Banks</span>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section section-dark" data-testid="global-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Global Reach</span>
            <h2>Global Presence</h2>
            <p className="section-intro">
              Delivering excellence across continents with local expertise
            </p>
          </div>

          <div className="global-map-container">
            <div className="world-map-wrapper">
              {/* SVG World Map */}
              <svg viewBox="0 0 1000 500" className="world-map-svg">
                {/* Map Background Grid */}
                <defs>
                  <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(45, 212, 191, 0.05)" strokeWidth="0.5"/>
                  </pattern>
                  <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.2"/>
                    <stop offset="50%" stopColor="#2dd4bf" stopOpacity="0.8"/>
                    <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0.2"/>
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <rect width="1000" height="500" fill="url(#grid)"/>
                
                {/* Simplified Continent Outlines */}
                {/* North America */}
                <path d="M150,80 Q200,60 250,80 L280,120 Q290,160 270,200 L220,220 Q180,200 160,160 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* South America */}
                <path d="M220,240 Q250,230 270,260 L260,340 Q240,380 220,360 L200,300 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* Europe */}
                <path d="M440,80 Q480,70 520,90 L540,130 Q530,160 500,150 L450,140 Q430,120 440,80 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* Africa */}
                <path d="M460,180 Q500,170 540,190 L550,280 Q530,340 490,330 L450,280 Q440,220 460,180 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* Middle East */}
                <path d="M560,140 Q600,130 640,160 L620,200 Q580,210 560,180 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* India */}
                <path d="M660,160 Q700,150 720,180 L710,240 Q680,270 660,240 L650,200 Z" 
                      fill="rgba(245, 158, 11, 0.15)" stroke="rgba(245, 158, 11, 0.5)" strokeWidth="2"/>
                {/* Southeast Asia */}
                <path d="M740,180 Q780,170 820,200 L830,260 Q800,280 760,260 L740,220 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                {/* Australia */}
                <path d="M780,320 Q830,310 870,340 L860,390 Q820,410 780,380 Z" 
                      fill="rgba(45, 212, 191, 0.1)" stroke="rgba(45, 212, 191, 0.3)" strokeWidth="1"/>
                
                {/* Connection Lines from India HQ */}
                <path d="M685,200 Q500,100 200,140" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                <path d="M685,200 Q600,150 480,120" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                <path d="M685,200 Q580,200 500,260" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                <path d="M685,200 Q650,180 600,170" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                <path d="M685,200 Q750,220 790,230" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                <path d="M685,200 Q750,300 820,350" fill="none" stroke="url(#connectionGradient)" strokeWidth="2" className="connection-line"/>
                
                {/* Location Markers */}
                {/* Americas */}
                <circle cx="200" cy="140" r="8" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
                <circle cx="200" cy="140" r="16" fill="none" stroke="#2dd4bf" strokeWidth="1" className="pulse-ring"/>
                {/* Europe */}
                <circle cx="480" cy="120" r="8" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
                <circle cx="480" cy="120" r="16" fill="none" stroke="#2dd4bf" strokeWidth="1" className="pulse-ring"/>
                {/* Africa */}
                <circle cx="500" cy="260" r="8" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
                <circle cx="500" cy="260" r="16" fill="none" stroke="#2dd4bf" strokeWidth="1" className="pulse-ring"/>
                {/* Middle East */}
                <circle cx="600" cy="170" r="8" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
                <circle cx="600" cy="170" r="16" fill="none" stroke="#2dd4bf" strokeWidth="1" className="pulse-ring"/>
                {/* India HQ - Gold/Larger */}
                <circle cx="685" cy="200" r="12" fill="#f59e0b" filter="url(#glow)" className="location-dot hq"/>
                <circle cx="685" cy="200" r="24" fill="none" stroke="#f59e0b" strokeWidth="2" className="pulse-ring hq"/>
                <circle cx="685" cy="200" r="36" fill="none" stroke="#f59e0b" strokeWidth="1" className="pulse-ring hq delay"/>
                {/* Southeast Asia */}
                <circle cx="790" cy="230" r="8" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
                <circle cx="790" cy="230" r="16" fill="none" stroke="#2dd4bf" strokeWidth="1" className="pulse-ring"/>
                {/* Australia */}
                <circle cx="820" cy="350" r="6" fill="#2dd4bf" filter="url(#glow)" className="location-dot"/>
              </svg>
              
              {/* Region Labels */}
              <div className="map-labels">
                <div className="map-label" style={{top: '25%', left: '18%'}}>
                  <span className="label-dot"></span>
                  <span className="label-text">Americas</span>
                </div>
                <div className="map-label" style={{top: '20%', left: '46%'}}>
                  <span className="label-dot"></span>
                  <span className="label-text">Europe</span>
                </div>
                <div className="map-label" style={{top: '50%', left: '48%'}}>
                  <span className="label-dot"></span>
                  <span className="label-text">Africa</span>
                </div>
                <div className="map-label" style={{top: '30%', left: '58%'}}>
                  <span className="label-dot"></span>
                  <span className="label-text">Middle East</span>
                </div>
                <div className="map-label hq" style={{top: '38%', left: '67%'}}>
                  <span className="label-dot gold"></span>
                  <span className="label-text">India (HQ)</span>
                </div>
                <div className="map-label" style={{top: '44%', left: '78%'}}>
                  <span className="label-dot"></span>
                  <span className="label-text">SE Asia</span>
                </div>
              </div>
            </div>
          </div>

          <div className="global-stats">
            <div className="global-stat">
              <span className="global-stat-value">45+</span>
              <span className="global-stat-label">Countries</span>
            </div>
            <div className="global-stat">
              <span className="global-stat-value">70+</span>
              <span className="global-stat-label">Banking Clients</span>
            </div>
            <div className="global-stat">
              <span className="global-stat-value">100+</span>
              <span className="global-stat-label">Projects</span>
            </div>
            <div className="global-stat">
              <span className="global-stat-value">24/7</span>
              <span className="global-stat-label">Global Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Client Success */}
      <section className="section section-navy" data-testid="clients-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Client Success</span>
            <h2>Trusted by Leading Financial Institutions</h2>
          </div>

          <div className="clients-carousel">
            <div className="clients-track">
              {['PRASAC', 'Chip Mong Bank', 'NBC Cambodia', 'Sathapana Bank', 'NKB Bank', 'Omni Finance'].map((client, index) => (
                <div key={index} className="client-logo">
                  <span>{client}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="case-studies-grid">
            <div className="case-study-card glass-card">
              <div className="case-study-tag">Case Study</div>
              <h4>Oracle FLEXCUBE Implementation</h4>
              <p>Complete core banking transformation for a leading microfinance institution</p>
              <button className="link-btn">
                Read Case Study <ArrowRight size={16} />
              </button>
            </div>
            <div className="case-study-card glass-card">
              <div className="case-study-tag">Case Study</div>
              <h4>OBDX Digital Banking</h4>
              <p>Digital banking platform deployment for enhanced customer experience</p>
              <button className="link-btn">
                Read Case Study <ArrowRight size={16} />
              </button>
            </div>
            <div className="case-study-card glass-card">
              <div className="case-study-tag">Case Study</div>
              <h4>Managed Services</h4>
              <p>24/7 application management for a regional bank</p>
              <button className="link-btn">
                Read Case Study <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Business Divisions */}
      <section className="section section-dark" data-testid="divisions-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Our Business</span>
            <h2>Our Business Divisions</h2>
            <p className="section-intro">Three pillars of enterprise excellence</p>
          </div>

          <div className="divisions-grid">
            <div className="division-card glass-card primary">
              <div className="division-icon">
                <Building2 size={40} />
              </div>
              <div className="division-tag">~70% Focus</div>
              <h3>Banking Technology Services</h3>
              <ul className="division-list">
                <li><CheckCircle2 size={16} /> Enterprise Consulting</li>
                <li><CheckCircle2 size={16} /> Oracle Banking Solutions</li>
                <li><CheckCircle2 size={16} /> Managed Services</li>
                <li><CheckCircle2 size={16} /> Implementation & Support</li>
                <li><CheckCircle2 size={16} /> Staff Augmentation</li>
                <li><CheckCircle2 size={16} /> Digital Banking</li>
              </ul>
              <button className="btn btn-primary">
                Explore Services <ArrowRight size={18} />
              </button>
            </div>

            <div className="division-card glass-card">
              <div className="division-icon">
                <Users size={40} />
              </div>
              <div className="division-tag">~20% Focus</div>
              <h3>Talent Solutions</h3>
              <ul className="division-list">
                <li><CheckCircle2 size={16} /> Career Accelerator Program (CAP)</li>
                <li><CheckCircle2 size={16} /> Associate Banking Technology Program</li>
                <li><CheckCircle2 size={16} /> Industry Integrated Degree</li>
                <li><CheckCircle2 size={16} /> Corporate Learning Solutions</li>
                <li><CheckCircle2 size={16} /> University Partnerships</li>
              </ul>
              <button className="btn btn-outline" onClick={() => scrollToSection('talent-programs')}>
                Explore Programs <ArrowRight size={18} />
              </button>
            </div>

            <div className="division-card glass-card">
              <div className="division-icon">
                <Lightbulb size={40} />
              </div>
              <div className="division-tag">~10% Focus</div>
              <h3>Research & Innovation</h3>
              <ul className="division-list">
                <li><CheckCircle2 size={16} /> AI & Banking Innovation</li>
                <li><CheckCircle2 size={16} /> FinTech Labs</li>
                <li><CheckCircle2 size={16} /> Automation Solutions</li>
                <li><CheckCircle2 size={16} /> Digital Transformation</li>
                <li><CheckCircle2 size={16} /> Emerging Technologies</li>
              </ul>
              <button className="btn btn-outline">
                Explore Innovation <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Building the Future Banking Workforce */}
      <section id="talent" className="section section-navy" data-testid="talent-section">
        <div className="container">
          <div className="talent-content">
            <div className="talent-text">
              <span className="overline">Talent Solutions</span>
              <h2>Building the Future Banking Workforce</h2>
              <blockquote className="talent-quote">
                "Trempplin is an enterprise Banking Technology company. To address the 
                shortage of industry-ready professionals, we established our Talent Solutions 
                division to develop future Banking Technology experts through enterprise 
                exposure, professional mentoring and real-world project experience."
              </blockquote>
              <p>
                Our Talent Programs are part of Trempplin's long-term workforce strategy 
                and complement our global consulting and technology services.
              </p>
              <button className="btn btn-primary" onClick={() => scrollToSection('talent-programs')}>
                Explore Talent Solutions <ArrowRight size={18} />
              </button>
            </div>

            <div className="talent-features">
              <div className="talent-feature">
                <Building2 size={24} />
                <h4>Enterprise Exposure</h4>
                <p>Real banking technology projects</p>
              </div>
              <div className="talent-feature">
                <Users size={24} />
                <h4>Professional Mentorship</h4>
                <p>Industry expert guidance</p>
              </div>
              <div className="talent-feature">
                <Briefcase size={24} />
                <h4>Real-World Projects</h4>
                <p>Hands-on experience</p>
              </div>
              <div className="talent-feature">
                <TrendingUp size={24} />
                <h4>Career Outcomes</h4>
                <p>Guaranteed placements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Talent Programs Details */}
      <section id="talent-programs" className="section section-dark" data-testid="talent-programs-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Our Programs</span>
            <h2>Talent Development Programs</h2>
            <p className="section-intro">Comprehensive programs designed to create industry-ready banking technology professionals</p>
          </div>

          <div className="talent-programs-grid">
            {/* CAP - Career Accelerator Program */}
            <div className="talent-program-card featured" data-testid="program-card-cap">
              <div className="program-badge">100% Career Guarantee</div>
              <div className="program-header">
                <div className="program-icon">
                  <Rocket size={32} />
                </div>
                <h3>Career Accelerator Program (CAP)</h3>
                <p className="program-tagline">DAY-1 JOB • DAY-1 SALARY • 100% Career Guarantee</p>
              </div>
              
              <div className="cap-highlight-banner">
                <div className="cap-highlight-text">
                  <span className="cap-big-text">Start Earning ₹25,000/month from Day 1</span>
                  <span className="cap-sub-text">While being trained inside real Banking & FinTech projects</span>
                </div>
              </div>

              <div className="program-body">
                <div className="program-details cap-details">
                  <div className="detail-item">
                    <Clock size={18} />
                    <span>12 Months Duration</span>
                  </div>
                  <div className="detail-item">
                    <MapPin size={18} />
                    <span>Premium Tech Park, Bangalore</span>
                  </div>
                  <div className="detail-item">
                    <Briefcase size={18} />
                    <span>On-Job Training (OJT)</span>
                  </div>
                  <div className="detail-item">
                    <DollarSign size={18} />
                    <span>Program Fee: ₹5,00,000</span>
                  </div>
                </div>

                <div className="cap-salary-progression">
                  <h4>Salary Progression:</h4>
                  <div className="salary-timeline">
                    <div className="salary-year">
                      <div className="salary-badge year1">Year 1</div>
                      <div className="salary-amount">₹3 LPA</div>
                      <div className="salary-monthly">₹25,000/month</div>
                    </div>
                    <div className="salary-arrow">
                      <ArrowRight size={24} />
                    </div>
                    <div className="salary-year">
                      <div className="salary-badge year2">Year 2</div>
                      <div className="salary-amount">₹6 LPA</div>
                      <div className="salary-monthly">₹40,000-50,000/month</div>
                    </div>
                  </div>
                </div>

                <div className="cap-two-columns">
                  <div className="program-features">
                    <h4>Program Highlights:</h4>
                    <ul>
                      <li><CheckCircle2 size={16} /> Guaranteed Day-1 Job Offer</li>
                      <li><CheckCircle2 size={16} /> On-Job Training Mode (OJT)</li>
                      <li><CheckCircle2 size={16} /> Onsite Opportunity within 1st Year</li>
                      <li><CheckCircle2 size={16} /> Deployment to Oracle, Cognizant, TCS</li>
                      <li><CheckCircle2 size={16} /> Industry: Digital Banking • FinTech • BFSI</li>
                    </ul>
                  </div>
                  <div className="program-features">
                    <h4>What You Will Learn:</h4>
                    <ul>
                      <li><CheckCircle2 size={16} /> Core Banking Technology</li>
                      <li><CheckCircle2 size={16} /> Digital Banking Platforms</li>
                      <li><CheckCircle2 size={16} /> FinTech Systems & Integrations</li>
                      <li><CheckCircle2 size={16} /> Banking Analytics & Risk</li>
                      <li><CheckCircle2 size={16} /> Payments & API Banking</li>
                      <li><CheckCircle2 size={16} /> Cloud & Modern Banking Architecture</li>
                    </ul>
                  </div>
                </div>

                <div className="cap-eligibility">
                  <h4>Eligibility:</h4>
                  <p>Any UG/PG • Any Stream • Any Percentage • Any Year of Passing</p>
                </div>
              </div>

              <div className="cap-cta-section">
                <a href="tel:+918310756135" className="btn btn-gold cap-phone-btn">
                  <Phone size={18} /> Call: +91 83107 56135
                </a>
                <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
                  Apply for CAP <ArrowRight size={18} />
                </button>
              </div>
            </div>

            {/* Associate Banking Technology Program */}
            <div className="talent-program-card" data-testid="program-card-associate">
              <div className="program-header">
                <div className="program-icon">
                  <Briefcase size={32} />
                </div>
                <h3>Associate Banking Technology Program</h3>
                <p className="program-tagline">Entry-level banking technology roles</p>
              </div>
              <div className="program-body">
                <div className="program-details">
                  <div className="detail-item">
                    <Clock size={18} />
                    <span>3-6 Months Duration</span>
                  </div>
                  <div className="detail-item">
                    <GraduationCap size={18} />
                    <span>For IT Graduates</span>
                  </div>
                </div>
                <div className="program-features">
                  <h4>Program Highlights:</h4>
                  <ul>
                    <li><CheckCircle2 size={16} /> Core Banking Fundamentals</li>
                    <li><CheckCircle2 size={16} /> Technical Skills Development</li>
                    <li><CheckCircle2 size={16} /> Project-Based Learning</li>
                    <li><CheckCircle2 size={16} /> Industry Certification</li>
                  </ul>
                </div>
              </div>
              <button className="btn btn-outline" onClick={() => scrollToSection('contact')}>
                Learn More <ArrowRight size={18} />
              </button>
            </div>

            {/* Industry Integrated Degree */}
            <div className="talent-program-card">
              <div className="program-header">
                <div className="program-icon">
                  <GraduationCap size={32} />
                </div>
                <h3>Industry Integrated Degree</h3>
                <p className="program-tagline">Academic excellence with industry exposure</p>
              </div>
              <div className="program-body">
                <div className="program-details">
                  <div className="detail-item">
                    <Clock size={18} />
                    <span>3-4 Years Duration</span>
                  </div>
                  <div className="detail-item">
                    <Building size={18} />
                    <span>University Partnership</span>
                  </div>
                </div>
                <div className="program-features">
                  <h4>Program Highlights:</h4>
                  <ul>
                    <li><CheckCircle2 size={16} /> Degree + Industry Certification</li>
                    <li><CheckCircle2 size={16} /> Internships with Partner Banks</li>
                    <li><CheckCircle2 size={16} /> Industry Expert Faculty</li>
                    <li><CheckCircle2 size={16} /> Campus to Corporate Transition</li>
                  </ul>
                </div>
              </div>
              <button className="btn btn-outline" onClick={() => scrollToSection('contact')}>
                Learn More <ArrowRight size={18} />
              </button>
            </div>

            {/* Corporate Learning Solutions */}
            <div className="talent-program-card">
              <div className="program-header">
                <div className="program-icon">
                  <BookOpen size={32} />
                </div>
                <h3>Corporate Learning Solutions</h3>
                <p className="program-tagline">Upskill your banking technology workforce</p>
              </div>
              <div className="program-body">
                <div className="program-details">
                  <div className="detail-item">
                    <Clock size={18} />
                    <span>Customizable Duration</span>
                  </div>
                  <div className="detail-item">
                    <Building2 size={18} />
                    <span>For Banks & Enterprises</span>
                  </div>
                </div>
                <div className="program-features">
                  <h4>Training Areas:</h4>
                  <ul>
                    <li><CheckCircle2 size={16} /> Oracle FLEXCUBE Implementation</li>
                    <li><CheckCircle2 size={16} /> Digital Banking (OBDX)</li>
                    <li><CheckCircle2 size={16} /> Core Banking Operations</li>
                    <li><CheckCircle2 size={16} /> Custom Training Programs</li>
                  </ul>
                </div>
              </div>
              <button className="btn btn-outline" onClick={() => scrollToSection('contact')}>
                Contact Us <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section section-dark" data-testid="founder-section">
        <div className="container">
          <div className="founder-content">
            <div className="founder-image">
              <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/5u54yufb_KK_3.jpg" alt="Kalmesh Kallur - Founder & CEO" />
            </div>
            <div className="founder-text">
              <span className="overline">Our Story</span>
              <h2>Building the Future of Banking Technology</h2>
              <blockquote>
                "After years of working with global banks on enterprise technology projects, 
                I witnessed a fundamental gap: the industry lacked professionals who truly 
                understood both banking operations and technology implementation.
                <br /><br />
                In 2016, I founded Trempplin with a dual mission—to deliver world-class 
                banking technology services while simultaneously building the future workforce 
                that this industry desperately needs.
                <br /><br />
                Today, Trempplin serves banks across 45+ countries while our Talent Solutions 
                division develops the next generation of Banking Technology professionals. 
                This isn't just business—it's building the future of banking technology."
              </blockquote>
              <div className="founder-info">
                <h4>Kalmesh Kallur (KK)</h4>
                <p>Founder & CEO</p>
                <a href="https://www.linkedin.com/in/kalmesh-kallur-k-k-66abb1106/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={18} /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>

          <div className="founder-pillars">
            <div className="pillar">
              <Building2 size={28} />
              <h4>Enterprise Banking Experience</h4>
              <p>Decades of global banking technology leadership</p>
            </div>
            <div className="pillar">
              <Eye size={28} />
              <h4>Technology Vision</h4>
              <p>Pioneering digital transformation in banking</p>
            </div>
            <div className="pillar">
              <Users size={28} />
              <h4>Solving Industry Skill Gap</h4>
              <p>Building workforce for future banking needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Life at Trempplin */}
      <section className="section section-navy" data-testid="life-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Culture</span>
            <h2>Life at Trempplin</h2>
            <p className="section-intro">Where excellence meets opportunity</p>
          </div>

          <div className="life-grid">
            <div className="life-card">
              <Building2 size={32} />
              <h4>Modern Office</h4>
            </div>
            <div className="life-card">
              <Users size={32} />
              <h4>Leadership Sessions</h4>
            </div>
            <div className="life-card">
              <MessageSquare size={32} />
              <h4>Project Discussions</h4>
            </div>
            <div className="life-card">
              <Target size={32} />
              <h4>Daily Standups</h4>
            </div>
            <div className="life-card">
              <Briefcase size={32} />
              <h4>Client Workshops</h4>
            </div>
            <div className="life-card">
              <Sparkles size={32} />
              <h4>Corporate Events</h4>
            </div>
          </div>

          <div className="life-cta">
            <button className="btn btn-primary" onClick={() => scrollToSection('careers')}>
              Join Our Team <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Vision 2030 */}
      <section className="section section-vision" data-testid="vision-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Future</span>
            <h2>Vision 2030</h2>
            <p className="section-intro">Shaping the future of banking technology</p>
          </div>

          <div className="vision-grid">
            <div className="vision-card">
              <Globe size={32} />
              <h4>Global Banking Technology Leadership</h4>
              <p>Becoming the preferred partner for banks worldwide</p>
            </div>
            <div className="vision-card">
              <GraduationCap size={32} />
              <h4>University Partnerships</h4>
              <p>Expanding academic collaborations globally</p>
            </div>
            <div className="vision-card">
              <Building size={32} />
              <h4>Centres of Excellence</h4>
              <p>Establishing global innovation hubs</p>
            </div>
            <div className="vision-card">
              <Bot size={32} />
              <h4>AI & Research</h4>
              <p>Leading banking AI innovation</p>
            </div>
            <div className="vision-card">
              <BookOpen size={32} />
              <h4>Industry Integrated Programs</h4>
              <p>Scaling talent development initiatives</p>
            </div>
            <div className="vision-card">
              <Users size={32} />
              <h4>Global Workforce Development</h4>
              <p>Building 10,000+ banking professionals</p>
            </div>
            <div className="vision-card">
              <MonitorSmartphone size={32} />
              <h4>Digital Banking Excellence</h4>
              <p>Powering next-gen digital transformation</p>
            </div>
            <div className="vision-card">
              <Lightbulb size={32} />
              <h4>Sustainable Banking Future</h4>
              <p>Driving ESG in banking technology</p>
            </div>
          </div>

          <div className="vision-tagline">
            "Building the Future of Banking Technology"
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="section section-dark" data-testid="leadership-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Leadership</span>
            <h2>Visionary Leadership Team</h2>
          </div>

          <div className="leadership-grid">
            {/* CEO Featured Card */}
            <div className="ceo-featured-card glass-card">
              <div className="ceo-image">
                <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/5u54yufb_KK_3.jpg" alt="Kalmesh Kallur" />
              </div>
              <div className="ceo-content">
                <blockquote className="ceo-quote">
                  <Quote size={32} className="quote-icon" />
                  "At Trempplin, our vision is to be the global leader in Banking Technology and Talent Development. Through our Technology Services and Talent Solutions, we are committed to delivering cutting-edge Oracle Banking solutions while nurturing the next generation of banking professionals."
                </blockquote>
                <div className="ceo-info">
                  <h3>Kalmesh Kallur (KK)</h3>
                  <p className="ceo-title">Founder & CEO</p>
                  <a href="https://www.linkedin.com/in/kalmesh-kallur-k-k-66abb1106/" target="_blank" rel="noopener noreferrer" className="linkedin-btn">
                    <Linkedin size={18} />
                    View LinkedIn
                  </a>
                </div>
              </div>
            </div>

            {/* Team Members Grid */}
            <div className="team-grid">
              <div className="team-member">
                <div className="team-image">
                  <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/ewvu8lpw_WhatsApp%20Image%202026-03-05%20at%204.55.16%20PM.jpeg" alt="Laxmi Kallur" />
                </div>
                <h4>Laxmi Kallur</h4>
                <p>Director & HR Head</p>
              </div>
              <div className="team-member">
                <div className="team-image">
                  <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/oxgz2aki_Amrutha_Website_PIc.jpeg" alt="Amrutha S" />
                </div>
                <h4>Amrutha S</h4>
                <p>AVP – Key Accounts</p>
              </div>
              <div className="team-member">
                <div className="team-image">
                  <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/gyaj3o6m_Media.jpg" alt="Vijayalaxmi H" />
                </div>
                <h4>Vijayalaxmi H</h4>
                <p>AVP - HR</p>
              </div>

              <div className="team-member">
                <div className="team-image">
                  <img src="https://customer-assets.emergentagent.com/job_tremplin-hub/artifacts/jsr7fq9p_WhatsApp%20Image%202026-06-09%20at%203.11.35%20PM.jpeg" alt="Ezhil Kumar" />
                </div>
                <h4>Ezhil Kumar</h4>
                <p>VP - Talent Acquisition</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insights */}
      <section id="insights" className="section section-navy" data-testid="insights-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Insights</span>
            <h2>Latest Insights</h2>
          </div>

          <div className="insights-grid">
            <div className="insight-card glass-card">
              <div className="insight-tag">Article</div>
              <h4>Digital Banking Transformation Trends 2025</h4>
              <p>Exploring the future of digital banking and customer experience</p>
              <button className="link-btn">
                Read Article <ArrowRight size={16} />
              </button>
            </div>
            <div className="insight-card glass-card">
              <div className="insight-tag">Case Study</div>
              <h4>Oracle FLEXCUBE Success Story</h4>
              <p>How we transformed core banking for a leading MFI</p>
              <button className="link-btn">
                Read Case Study <ArrowRight size={16} />
              </button>
            </div>
            <div className="insight-card glass-card">
              <div className="insight-tag">White Paper</div>
              <h4>Future of Core Banking</h4>
              <p>Strategic insights on core banking modernization</p>
              <button className="link-btn">
                Download <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" className="section section-dark" data-testid="careers-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Careers</span>
            <h2>Build Your Career with a Global Banking Technology Leader</h2>
            <p className="section-intro">
              Join our team of Oracle Banking specialists and work on mission-critical projects for leading financial institutions worldwide.
            </p>
          </div>

          {/* Career Categories */}
          <div className="careers-grid">
            <div className="career-card">
              <Briefcase size={28} />
              <h4>Experienced Professionals</h4>
              <p>Join our global consulting team</p>
            </div>
            <div className="career-card">
              <GraduationCap size={28} />
              <h4>Graduate Programs</h4>
              <p>Start your banking tech career</p>
            </div>
            <div className="career-card">
              <Users size={28} />
              <h4>Associate Programs</h4>
              <p>Industry integrated learning</p>
            </div>
            <div className="career-card">
              <Rocket size={28} />
              <h4>Internship Programs</h4>
              <p>Real project experience</p>
            </div>
          </div>

          {/* Current Openings */}
          <div className="job-listings-section">
            <h3 className="job-listings-title">
              <Briefcase size={24} />
              Current Openings
            </h3>

            <div className="job-listings-grid">
              {/* FLEXCUBE Consultant */}
              <div className="job-card glass-card">
                <div className="job-header">
                  <div className="job-badge urgent">Hiring</div>
                  <h4>Oracle FLEXCUBE Consultant</h4>
                  <p className="job-location">
                    <MapPin size={14} />
                    Bengaluru, India / Remote
                  </p>
                </div>
                <div className="job-details">
                  <div className="job-meta">
                    <span><Clock size={14} /> Full-time</span>
                    <span><Award size={14} /> 3+ Years Experience</span>
                  </div>
                  <div className="job-skills">
                    <span className="skill-tag">FLEXCUBE</span>
                    <span className="skill-tag">OBMA</span>
                    <span className="skill-tag">OBTF</span>
                    <span className="skill-tag">Core Banking</span>
                  </div>
                  <p className="job-description">
                    We are looking for experienced FLEXCUBE consultants to work on implementation and support projects for our global banking clients.
                  </p>
                  <ul className="job-requirements">
                    <li>3+ years experience in Oracle FLEXCUBE Universal Banking</li>
                    <li>Strong knowledge of OBMA (Oracle Banking Microservices Architecture)</li>
                    <li>Experience with OBTF (Oracle Banking Trade Finance)</li>
                    <li>Excellent communication and client-facing skills</li>
                  </ul>
                </div>
                <button className="btn btn-primary btn-full" onClick={() => scrollToSection('contact')}>
                  Apply Now <ArrowRight size={16} />
                </button>
              </div>

              {/* OBDX Consultant */}
              <div className="job-card glass-card">
                <div className="job-header">
                  <div className="job-badge urgent">Hiring</div>
                  <h4>Oracle OBDX Consultant</h4>
                  <p className="job-location">
                    <MapPin size={14} />
                    Bengaluru, India / Remote
                  </p>
                </div>
                <div className="job-details">
                  <div className="job-meta">
                    <span><Clock size={14} /> Full-time</span>
                    <span><Award size={14} /> 3+ Years Experience</span>
                  </div>
                  <div className="job-skills">
                    <span className="skill-tag">OBDX</span>
                    <span className="skill-tag">Digital Banking</span>
                    <span className="skill-tag">API Integration</span>
                    <span className="skill-tag">JavaScript</span>
                  </div>
                  <p className="job-description">
                    Join our digital banking team to deliver Oracle OBDX implementations for banks across Asia, Middle East and Africa.
                  </p>
                  <ul className="job-requirements">
                    <li>3+ years experience in Oracle OBDX Digital Experience</li>
                    <li>Strong understanding of digital banking channels</li>
                    <li>Experience with REST APIs and microservices</li>
                    <li>Knowledge of JavaScript/Angular frameworks</li>
                  </ul>
                </div>
                <button className="btn btn-primary btn-full" onClick={() => scrollToSection('contact')}>
                  Apply Now <ArrowRight size={16} />
                </button>
              </div>

              {/* OFSAA Consultant */}
              <div className="job-card glass-card">
                <div className="job-header">
                  <div className="job-badge">Senior Role</div>
                  <h4>Oracle OFSAA Consultant</h4>
                  <p className="job-location">
                    <MapPin size={14} />
                    Bengaluru, India / Remote
                  </p>
                </div>
                <div className="job-details">
                  <div className="job-meta">
                    <span><Clock size={14} /> Full-time</span>
                    <span><Award size={14} /> 5+ Years Experience</span>
                  </div>
                  <div className="job-skills">
                    <span className="skill-tag">OFSAA</span>
                    <span className="skill-tag">Financial Analytics</span>
                    <span className="skill-tag">Risk Management</span>
                    <span className="skill-tag">Regulatory Reporting</span>
                  </div>
                  <p className="job-description">
                    We need senior OFSAA specialists to lead analytics and risk management implementations for enterprise banking clients.
                  </p>
                  <ul className="job-requirements">
                    <li>5+ years experience in Oracle OFSAA suite</li>
                    <li>Strong expertise in ALM, FTP, and Risk modules</li>
                    <li>Experience with regulatory reporting (Basel, IFRS)</li>
                    <li>Ability to lead client engagements independently</li>
                  </ul>
                </div>
                <button className="btn btn-primary btn-full" onClick={() => scrollToSection('contact')}>
                  Apply Now <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Why Join Trempplin */}
          <div className="why-join-section">
            <h3>Why Join Trempplin?</h3>
            <div className="why-join-grid">
              <div className="why-join-item">
                <Globe size={24} />
                <span>Work with Global Banks</span>
              </div>
              <div className="why-join-item">
                <TrendingUp size={24} />
                <span>Career Growth</span>
              </div>
              <div className="why-join-item">
                <Users size={24} />
                <span>Expert Mentorship</span>
              </div>
              <div className="why-join-item">
                <Award size={24} />
                <span>Competitive Pay</span>
              </div>
              <div className="why-join-item">
                <Briefcase size={24} />
                <span>Diverse Projects</span>
              </div>
              <div className="why-join-item">
                <Heart size={24} />
                <span>Work-Life Balance</span>
              </div>
            </div>
          </div>

          <div className="careers-cta">
            <p>Don't see a role that fits? Send us your resume and we'll keep you in mind for future opportunities.</p>
            <button className="btn btn-outline" onClick={() => scrollToSection('contact')}>
              Submit Your Resume <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="section section-navy" data-testid="partners-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">Partners</span>
            <h2>Strategic Partnerships</h2>
          </div>

          <div className="partners-content">
            <div className="partner-highlight">
              <div className="partner-badge">
                <Database size={40} />
              </div>
              <h3>Oracle Partner</h3>
              <p>
                As an Oracle Partner, Trempplin delivers comprehensive Oracle Banking 
                solutions including FLEXCUBE, OBDX, OFSAA, and the complete Oracle 
                Financial Services product suite.
              </p>
            </div>

            <div className="partners-categories">
              <div className="partner-category">
                <h4>Technology Partners</h4>
                <p>Leading technology alliances</p>
              </div>
              <div className="partner-category">
                <h4>University Partners</h4>
                <p>Academic collaborations</p>
              </div>
              <div className="partner-category">
                <h4>Consulting Partners</h4>
                <p>Strategic consulting alliances</p>
              </div>
            </div>
          </div>

          {/* Strategic Partner Alliance CTA */}
          <div className="partner-alliance-cta">
            <div className="alliance-cta-content">
              <h3>Become a Strategic Partner</h3>
              <p>Join the Trempplin Strategic Partner Alliance Program and access enterprise opportunities, joint bidding, and business development collaboration.</p>
              <a href="/strategic-partner-alliance" className="btn btn-gold">
                Explore Partnership Program <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="section section-cta" data-testid="cta-section">
        <div className="container">
          <h2>Ready to Transform Your Banking Technology?</h2>
          <p>Partner with Trempplin for enterprise-grade banking solutions</p>
          <div className="cta-buttons">
            <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Schedule Consultation
            </button>
            <button className="btn btn-outline-light" onClick={() => scrollToSection('contact')}>
              Partner With Us
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section section-dark" data-testid="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <span className="overline">Contact</span>
              <h2>Let's Discuss Your Banking Technology Needs</h2>
              
              <div className="contact-cards">
                <div className="contact-card">
                  <MapPin size={20} />
                  <div>
                    <h4>Office</h4>
                    <p>No.4/4, 1, 1st Main Rd, Industrial Suburb, Rajaji Nagar, Bengaluru, Karnataka 560010</p>
                  </div>
                </div>
                <div className="contact-card">
                  <Mail size={20} />
                  <div>
                    <h4>Email</h4>
                    <p>contactus@trempplin.com</p>
                  </div>
                </div>
                <div className="contact-card">
                  <Phone size={20} />
                  <div>
                    <h4>Phone</h4>
                    <p>+91 80 2315 4747</p>
                  </div>
                </div>
              </div>

              <div className="contact-map">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.6951068498883!2d77.5483!3d12.9912!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDU5JzI4LjMiTiA3N8KwMzInNTMuOSJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%" 
                  height="200" 
                  style={{border: 0, borderRadius: '12px'}} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Trempplin Office Location"
                ></iframe>
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=12.9912,77.5483" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="directions-btn"
                >
                  <MapPin size={16} /> Get Directions
                </a>
              </div>
            </div>

            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
                <h3>Send Us a Message</h3>
                
                {formStatus.message && (
                  <div className={`form-status ${formStatus.type}`}>
                    {formStatus.message}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      data-testid="contact-phone"
                    />
                  </div>
                  <div className="form-group">
                    <label>Company / Organization</label>
                    <input 
                      type="text" 
                      name="company" 
                      value={formData.company}
                      onChange={handleInputChange}
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Service Interest</label>
                  <select 
                    name="service" 
                    value={formData.service}
                    onChange={handleInputChange}
                    data-testid="contact-service"
                  >
                    <option value="">Select a service</option>
                    <optgroup label="Enterprise Services">
                      <option value="oracle-banking">Oracle Banking Solutions</option>
                      <option value="managed-services">Managed Services</option>
                      <option value="digital-banking">Digital Banking</option>
                      <option value="staff-augmentation">Staff Augmentation</option>
                      <option value="implementation">Implementation Services</option>
                      <option value="consulting">Banking Consulting</option>
                    </optgroup>
                    <optgroup label="Talent Solutions">
                      <option value="cap">Career Accelerator Program</option>
                      <option value="associate-program">Associate Program</option>
                      <option value="corporate-learning">Corporate Learning</option>
                      <option value="university-partnership">University Partnership</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="careers">Career Opportunities</option>
                      <option value="other">Other</option>
                    </optgroup>
                  </select>
                </div>

                <div className="form-group">
                  <label>Message *</label>
                  <textarea 
                    name="message" 
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-message"
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting} data-testid="contact-submit">
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          {/* Top Footer - Credentials Bar */}
          <div className="footer-credentials">
            <div className="credential-item">
              <Shield size={24} />
              <div>
                <span className="credential-title">ISO 27001:2022</span>
                <span className="credential-subtitle">Information Security</span>
              </div>
            </div>
            <div className="credential-item">
              <Award size={24} />
              <div>
                <span className="credential-title">Oracle Partner</span>
                <span className="credential-subtitle">Banking Solutions</span>
              </div>
            </div>
            <div className="credential-item">
              <Globe size={24} />
              <div>
                <span className="credential-title">45+ Countries</span>
                <span className="credential-subtitle">Global Operations</span>
              </div>
            </div>
            <div className="credential-item">
              <Users size={24} />
              <div>
                <span className="credential-title">70+ Banks</span>
                <span className="credential-subtitle">Enterprise Clients</span>
              </div>
            </div>
          </div>

          {/* Main Footer Grid */}
          <div className="footer-main">
            <div className="footer-brand">
              <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" className="footer-logo" />
              <p className="footer-tagline">Global Banking Technology & FinTech Company</p>
              <p className="footer-desc">Empowering financial institutions worldwide with cutting-edge Oracle Banking solutions, digital transformation, and industry-ready talent.</p>
              
              {/* Oracle Expertise Badges */}
              <div className="oracle-badges">
                <span className="oracle-badge">FLEXCUBE</span>
                <span className="oracle-badge">OBDX</span>
                <span className="oracle-badge">OFSAA</span>
                <span className="oracle-badge">OBMA</span>
              </div>
              
              {/* Social Links */}
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/trempplin/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
                <a href="https://www.instagram.com/trempplin/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
                <a href="https://www.facebook.com/trempplin" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={20} /></a>
                <a href="https://twitter.com/trempplin" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><Twitter size={20} /></a>
                <a href="https://www.youtube.com/@trempplin" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><Youtube size={20} /></a>
              </div>
            </div>

            <div className="footer-links">
              <h4>Enterprise Services</h4>
              <button onClick={() => scrollToSection('services')}>Oracle Banking Solutions</button>
              <button onClick={() => scrollToSection('services')}>Digital Banking (OBDX)</button>
              <button onClick={() => scrollToSection('services')}>Managed Services</button>
              <button onClick={() => scrollToSection('services')}>Staff Augmentation</button>
              <button onClick={() => scrollToSection('services')}>Implementation & Support</button>
              <button onClick={() => scrollToSection('services')}>Banking Consulting</button>
            </div>

            <div className="footer-links">
              <h4>Talent Solutions</h4>
              <button onClick={() => scrollToSection('talent')}>Career Accelerator Program</button>
              <button onClick={() => scrollToSection('talent')}>Associate Program</button>
              <button onClick={() => scrollToSection('talent')}>Industry Integrated Degree</button>
              <button onClick={() => scrollToSection('talent')}>Corporate Learning</button>
              <button onClick={() => scrollToSection('careers')}>Current Openings</button>
            </div>

            <div className="footer-links">
              <h4>Quick Links</h4>
              <button onClick={() => scrollToSection('about')}>About Us</button>
              <button onClick={() => scrollToSection('founder-section')}>Our Story</button>
              <button onClick={() => scrollToSection('leadership')}>Leadership</button>
              <button onClick={() => scrollToSection('clients-section')}>Case Studies</button>
              <button onClick={() => scrollToSection('insights')}>Insights & News</button>
              <button onClick={() => scrollToSection('global-section')}>Global Presence</button>
            </div>

            <div className="footer-contact">
              <h4>Contact Us</h4>
              <div className="contact-item">
                <MapPin size={16} />
                <div>
                  <strong>Headquarters</strong>
                  <p>Bengaluru, Karnataka, India</p>
                </div>
              </div>
              <div className="contact-item">
                <Mail size={16} />
                <div>
                  <strong>Email</strong>
                  <a href="mailto:contactus@trempplin.com">contactus@trempplin.com</a>
                </div>
              </div>
              <div className="contact-item">
                <Phone size={16} />
                <div>
                  <strong>Phone</strong>
                  <a href="tel:+918023154747">+91 80 2315 4747</a>
                </div>
              </div>
              <button onClick={() => scrollToSection('contact')} className="footer-cta">
                Get In Touch <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="footer-bottom">
            <div className="footer-copyright">
              <p>&copy; {new Date().getFullYear()} Trempplin Technologies Pvt. Ltd. All rights reserved.</p>
            </div>
            <div className="footer-legal">
              <button>Privacy Policy</button>
              <span className="divider">|</span>
              <button>Terms of Use</button>
              <span className="divider">|</span>
              <button>Cookie Policy</button>
              <span className="divider">|</span>
              <button>Sitemap</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
